import { Core, Decorative } from "@faire/design-tokens";
import { trackProductInfoProductDetailsCustomizeItemToggleClick } from "@faire/web-api/events/productInfo/click/productDetailsCustomizeItemToggle";
import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { IItemPromoPrice } from "@faire/web-api/indigofair/data/IItemPromoPrice";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { formatMoney } from "@faire/web/common/money";
import { Checkbox } from "@faire/web/slate/Checkbox";
import { TextArea } from "@faire/web/slate/Input/TextArea";
import { Row, Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography, Span } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { showProductPromotions } from "@faire/retailer/lib/showProductPromotions";
import { useSettingProductCustomizationsBuildathon } from "@faire/retailer/settings/useSettingProductCustomizationsBuildathon";

export declare namespace Customization {
  interface IProps {
    required?: boolean;
    value: string;
    onChange: (value: string) => void;
    characterLimit?: number;
    additionalCharge: IFaireMoney;
    additionalPromoCharge?: IItemPromoPrice;
    instructions: string;
    /**
     * Note this will fire on mount with a value based on the initial `value` passed to it.
     */
    onToggle: (active: boolean) => void;
    hideAdditionalCharge?: boolean;
  }
}

// max length allowed by DB
const INPUT_CHARACTERS_HARD_LIMIT = 1024;

const CustomizationBase: React.FC<Customization.IProps> = ({
  value,
  onChange,
  onToggle,
  instructions,
  characterLimit,
  additionalCharge,
  hideAdditionalCharge,
  additionalPromoCharge,
  required,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const isUpdatedProductCustomizationEnabled =
    useSettingProductCustomizationsBuildathon();
  const [isChecked, setIsChecked] = useState(
    value !== "" || isUpdatedProductCustomizationEnabled
  );
  const hasToggledInitially = useRef(false);

  useEffect(() => {
    if (!hasToggledInitially.current) {
      onToggle(isChecked);
      hasToggledInitially.current = true;
    }
  }, [isChecked, onToggle]);

  const onCheckboxChange = () => {
    if (!isChecked) {
      trackProductInfoProductDetailsCustomizeItemToggleClick();
    }
    setIsChecked((prev) => {
      onToggle(!prev);
      return !prev;
    });
    onChange("");
  };

  const labelText = instructions;

  const errorMessage =
    value.trim().length === 0
      ? strictLocalize({
          defaultMessage: "This field is required",
          description: {
            text: "Error message shown below a text area indicating that a value must be entered for a customization option.",
          },
        })
      : undefined;

  // Passing a maxCharacters of 0 to TextAreaInput implies
  // unbounded length. This prevents that from causing errors
  // in the BE.
  const maxCharacters =
    characterLimit === 0 ? INPUT_CHARACTERS_HARD_LIMIT : characterLimit;

  if (required) {
    return (
      <Container>
        <Typography>
          <StrictLocalMsg
            defaultMessage="Customize this item"
            description={{ text: "add modifications to this item" }}
          />
        </Typography>
        <Spacer size={10} />
        <TextArea
          label={labelText}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxCharacters}
          required
          error={errorMessage}
          style={{ height: "125px" }}
          data-test-id="addToCartCustomizationRequiredTextArea"
        />
      </Container>
    );
  } else {
    return (
      <>
        <Container>
          <Column>
            <CheckboxContainer>
              <Checkbox
                checked={isChecked}
                onChange={onCheckboxChange}
                data-test-id="addToCartCustomizationCheckbox"
              />
              <Spacer size="1x" />
              <CheckboxLabel onClick={onCheckboxChange}>
                {hideAdditionalCharge ? null : (
                  <Typography data-test-id="customization-additional-charge">
                    {showProductPromotions(additionalPromoCharge) ? (
                      <StrictLocalMsg
                        defaultMessage="Customize this item for <Strikethrough>{additionalCharge}</Strikethrough> <Gold>{additionalPromoCharge}</Gold>/unit"
                        description={{
                          text: "additional promotion cost for customizing a product, e.g. £̶1̶.̶0̶0̶ £0.50/unit",
                        }}
                        values={DEPRECATED_backwardsCompatibleValues({
                          Strikethrough: StrikeThroughFormatting,
                          Gold: GoldFormatting,
                          additionalCharge: formatMoney({
                            money: additionalCharge,
                          }),
                          additionalPromoCharge: formatMoney({
                            money: additionalPromoCharge.discounted_item_price,
                          }),
                        })}
                      />
                    ) : (
                      <StrictLocalMsg
                        defaultMessage="Customize this item for {additionalCharge}/unit"
                        description={{
                          text: "additional cost for customizing a product",
                        }}
                        values={DEPRECATED_backwardsCompatibleValues({
                          additionalCharge: formatMoney({
                            money:
                              additionalPromoCharge?.discounted_item_price ??
                              additionalCharge,
                          }),
                        })}
                      />
                    )}
                  </Typography>
                )}
              </CheckboxLabel>
            </CheckboxContainer>
            <Row>
              <Spacer size="3x" />
              <Typography color={Core.text.subdued} variant="labelSansRegular">
                <StrictLocalMsg defaultMessage="Customized items are not returnable" />
              </Typography>
            </Row>
          </Column>
          {isChecked ? (
            <>
              <Spacer size={10} />
              <TextArea
                label={labelText}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                maxLength={maxCharacters}
                required
                error={errorMessage}
                style={{ height: "125px" }}
                data-test-id="addToCartCustomizationOptionalTextArea"
              />
            </>
          ) : null}
        </Container>
      </>
    );
  }
};

export const Customization = observer(CustomizationBase);

const StrikeThroughFormatting = (chunk: JSX.Element) => (
  <StrikeThrough>{chunk}</StrikeThrough>
);

const GoldFormatting = (chunk: JSX.Element) => (
  <Span color={Decorative.shade.orange}>{chunk}</Span>
);

const Container = styled.div`
  width: 100%;
`;

const CheckboxContainer = styled(Row).attrs({ align: "center" })`
  flex-wrap: nowrap;
`;

const CheckboxLabel = styled.label`
  display: flex;
  justify-content: space-between;
  flex: 1 0 auto;
  cursor: pointer;
`;
const StrikeThrough = styled.span`
  text-decoration: line-through;
  color: ${Core.text.subdued};
`;
